import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Switch } from '@material-ui/core';
import Imgix from 'shared/components/imgix';
import ChevronIcon from 'assets/chevron-icon';
import {
  CheckoutLabelGrouping,
  CheckoutLabel,
  CheckoutBody,
  CheckoutSectionHeading,
  CheckoutSection,
} from 'checkout/components/styles/common';
import { tracker } from 'src/utils/analytics';
import { useIntersectionObserver } from 'src/hooks/use-intersection-observer';
import useCart from 'hooks/use-cart';
import { useRoundupForCharity } from './use-roundup-for-charity';

type RoundupForCharityProps = {
  // 'large' bumps the checkout page's small type scale up to match a larger-format surface
  // (currently: the kiosk checkout modal, which otherwise uses a much bigger base
  // font/bold-uppercase-button visual scale).
  size?: 'default' | 'large';
  // Drops the "Make an impact" heading + shared checkout-page label/body column chrome
  // entirely, rendering just the charity card. For surfaces (like the kiosk order-summary
  // panel) that already supply their own heading/context and just need the compact card.
  hideHeading?: boolean;
  // Strips the charity card's own border/background so it reads as plain content flowing
  // inside a surface that already provides its own card/background (e.g. nested directly
  // inside the kiosk order-summary panel, rather than floating as its own separate card).
  bordered?: boolean;
  // Collapses the "why" copy (the charity description) behind its own chevron, with the
  // amount/charity name folded into a single always-visible toggle row instead of a separate
  // heading + always-expanded paragraph. For space-constrained surfaces (kiosk) where the full
  // always-expanded card competes with the rest of checkout for vertical room.
  collapsible?: boolean;
};

type RoundupForCharityContentProps = {
  activeCharity: ReturnType<typeof useRoundupForCharity>['activeCharity'];
  charityRoundupSelected: boolean;
  roundupAmount: number;
  large: boolean;
  hideHeading: boolean;
  bordered: boolean;
  collapsible: boolean;
};

export const RoundupForCharity = ({
  size = 'default',
  hideHeading = false,
  bordered = true,
  collapsible = false,
}: RoundupForCharityProps = {}): JSX.Element | null => {
  const { activeCharity, shouldShow, roundupAmount, charityRoundupSelected } = useRoundupForCharity();

  if (!shouldShow) {
    return null;
  }
  return (
    <RoundupForCharityContent
      activeCharity={activeCharity}
      charityRoundupSelected={charityRoundupSelected}
      roundupAmount={roundupAmount}
      large={size === 'large'}
      hideHeading={hideHeading}
      bordered={bordered}
      collapsible={collapsible}
    />
  );
};

const RoundupForCharityContent = ({
  activeCharity,
  charityRoundupSelected,
  roundupAmount,
  large,
  hideHeading,
  bordered,
  collapsible,
}: RoundupForCharityContentProps): JSX.Element | null => {
  const Cart = useCart();

  const handleRoundUpForCharityToggled = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const selected = event.target.checked;
    Cart.setCharityRoundupSelected(selected);
    tracker.roundupForCharityToggled({
      action: selected ? 'enabled' : 'disabled',
    });
  };

  const ref = useRef<HTMLDivElement>(null);
  const entry = useIntersectionObserver(ref, { threshold: 0.1, freezeOnceVisible: true });

  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (entry?.isIntersecting) {
      tracker.roundupForCharityViewed({});
    }
  }, [entry?.isIntersecting]);

  if (!activeCharity) {
    return null;
  }

  const charityCard = collapsible ? (
    <CharityContainer $large={large} $bordered={bordered}>
      {/* A real <input type="checkbox"> (the MUI Switch) can't legally nest inside a <button> --
          browsers handle it inconsistently and screen readers collapse the whole row into one
          control, hiding the checkbox. So this row is a plain div: the switch and the expand
          chevron are separate, sibling interactive controls instead of one nested inside the
          other. */}
      <CollapsibleHeaderRow>
        <ToggleContainer $large={large} $bold={false}>
          <StyledSwitch $large={large} checked={charityRoundupSelected} onChange={handleRoundUpForCharityToggled} />
          Round up by donating <b>${roundupAmount.toFixed(2)}</b> to {activeCharity.name}
        </ToggleContainer>
        <Imgix
          src={activeCharity.logoUrl}
          htmlAttributes={{ alt: `${activeCharity.name} Logo` }}
          width={large ? 80 : 65}
        />
        <ExpandButton
          type='button'
          aria-expanded={expanded}
          aria-label={`${expanded ? 'Hide' : 'Show'} details about ${activeCharity.name}`}
          onClick={() => setExpanded((isExpanded) => !isExpanded)}
        >
          <ControlIcon width={20} height={20} $open={expanded} />
        </ExpandButton>
      </CollapsibleHeaderRow>
      {expanded && <RoundupDescription $large={large}>{activeCharity.description}</RoundupDescription>}
    </CharityContainer>
  ) : (
    <CharityContainer $large={large} $bordered={bordered}>
      <RoundupHeader>
        <ToggleContainer $large={large}>
          <StyledSwitch $large={large} checked={charityRoundupSelected} onChange={handleRoundUpForCharityToggled} />
          Round up for:
        </ToggleContainer>
        <Imgix
          src={activeCharity.logoUrl}
          htmlAttributes={{ alt: `${activeCharity.name} Logo` }}
          width={large ? 80 : 65}
        />
      </RoundupHeader>
      <RoundupDescription $large={large}>
        Round up your order by donating <b>${roundupAmount.toFixed(2)}</b> to{' '}
        <CharityLink target='_blank' href={activeCharity.websiteUrl}>
          {activeCharity.name}
        </CharityLink>
        . {activeCharity.description}
      </RoundupDescription>
    </CharityContainer>
  );

  if (hideHeading) {
    return <CompactContainer ref={ref}>{charityCard}</CompactContainer>;
  }

  return (
    <CheckoutSection ref={ref} noBorder={large}>
      <StyledCheckoutLabelGrouping id='charity' $large={large}>
        <StyledCheckoutLabel $large={large}>
          <StyledSectionHeading id='charitySectionHeading' $large={large}>
            Make an impact
          </StyledSectionHeading>
        </StyledCheckoutLabel>

        <StyledCheckoutBody $large={large}>{charityCard}</StyledCheckoutBody>
      </StyledCheckoutLabelGrouping>
    </CheckoutSection>
  );
};

const CompactContainer = styled.div`
  width: 100%;
`;

const StyledCheckoutLabelGrouping = styled(CheckoutLabelGrouping)<{ $large: boolean }>`
  ${({ $large }) =>
    $large &&
    `
      display: block;
      width: 100%;
      margin-bottom: 12px;
    `}
`;

const StyledCheckoutLabel = styled(CheckoutLabel)<{ $large: boolean }>`
  ${({ $large }) =>
    $large &&
    `
      display: block;
      max-width: 100%;
      padding: 0 0 8px;
      text-align: left;
    `}
`;

const StyledCheckoutBody = styled(CheckoutBody)<{ $large: boolean }>`
  ${({ $large }) =>
    $large &&
    `
      display: block;
      padding-left: 0;
    `}
`;

const StyledSectionHeading = styled(CheckoutSectionHeading)<{ $large: boolean }>`
  ${({ $large }) => $large && `font-size: 22px;`}
`;

const CharityContainer = styled.div<{ $large?: boolean; $bordered?: boolean }>`
  display: flex;
  max-width: ${({ $large }) => ($large ? '100%' : '390px')};
  padding: ${({ theme, $bordered }) => ($bordered ? theme.spaces[5] : 0)};
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spaces[5]};
  flex: 1 0 0;
  border-radius: ${({ $bordered }) => ($bordered ? 'var(--dcm-roundup-charity-card-radius, 5px)' : '0')};
  border: ${({ $bordered }) =>
    $bordered ? '1px solid var(--dcm-roundup-charity-card-border-color, #d8dee3)' : 'none'};
  background: ${({ theme, $bordered }) => ($bordered ? theme.colors.white : 'transparent')};
`;

const RoundupHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spaces[2]};
  align-self: stretch;
`;

const CollapsibleHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spaces[2]};
  align-self: stretch;
  width: 100%;
`;

// Icon-only, so it keeps the browser's default focus-visible outline (unlike CharityLink's
// sibling controls, nothing here resets `all` to strip it) -- it needs its own visible focus
// indicator since it's the only way to expand/collapse via keyboard.
const ExpandButton = styled.button`
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
`;

const ControlIcon = styled(ChevronIcon)<{ $open?: boolean }>`
  flex: 0 0 auto;
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.3s ease;
`;

const RoundupDescription = styled.p<{ $large?: boolean }>`
  color: ${({ theme }) => theme.colors.grey[30]};
  font-family: var(--dct--customized-fonts-secondary, ${({ theme }) => theme.customized.fonts.secondary});
  font-size: ${({ $large }) => ($large ? '16px' : '13px')};
  line-height: ${({ $large }) => ($large ? '24px' : '18px')};
  color: #646d72;
  /* The kiosk modal wrapper (v2-container.jsx) sets text-align: center on its own root, which
     cascades down (text-align is inherited) to any descendant that doesn't set its own --
     this paragraph needs its own explicit left alignment rather than relying on each caller's
     surrounding markup to happen to override it first. */
  text-align: left;
`;

const CharityLink = styled.a`
  text-decoration: underline;
`;

const ToggleContainer = styled.div<{ $large?: boolean; $bold?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spaces[3]};
  flex: 1 0 0;
  color: ${({ theme }) => theme.colors.grey[30]};
  font-family: var(--dct--customized-fonts-secondary, ${({ theme }) => theme.customized.fonts.secondary});
  font-size: ${({ $large }) => ($large ? '18px' : '14px')};
  font-weight: ${({ $bold = true }) => ($bold ? 'bold' : 'normal')};
  text-align: left;
`;

const StyledSwitch = styled(Switch)<{ $large?: boolean }>`
  &.MuiSwitch-root {
    width: ${({ $large }) => ($large ? '48px' : '40px')};
    height: ${({ $large }) => ($large ? '28px' : '24px')};
    border-radius: 14px;
    padding: 0px;

    .MuiSwitch-switchBase.Mui-checked {
      /* Distance from the track's left edge to its right edge, minus the thumb's own width
         and its 2px inset on each side (padding: 2px on .MuiButtonBase-root below) -- i.e.
         how far the thumb has to travel to sit flush against the right edge when "on".
         Tied to $large's track width (48px vs 40px) rather than a single fixed value, which
         previously left the large kiosk thumb stalled short of the right edge -- still
         visibly "on" via color, but not visually flush/committed the way the switch implies. */
      transform: translateX(${({ $large }) => ($large ? '24px' : '16px')});
    }

    .MuiSwitch-track {
      background-color: ${({ theme }) => theme.colors.grey[75]};
      opacity: 1;
    }
    .Mui-checked + .MuiSwitch-track {
        background-color: var(--dct--customized-colors-buttonsLinks, ${({ theme }) =>
          theme.customized.colors.buttonsLinks});
        opacity: 1;
      }
    }

    .MuiSwitch-colorSecondary.Mui-checked:hover {
      background: none;
    }
    .MuiButtonBase-root {
      padding: 2px;
      /* MUI's default switchBase is positioned with top: 0 inside the track, which only
         centers it vertically when the (24px-tall, incl. this 2px padding) base happens to
         exactly match the track's own height. The default 40px/24px track does, by
         coincidence, but the larger 48px/28px kiosk track is 4px taller than the base, so
         top: 0 left an uneven 0px-top/4px-bottom gap -- the thumb/checkmark read as riding
         high instead of centered. top: 50% + a negative margin of half this element's own
         height centers it regardless of the track's height, so this isn't re-broken by any
         future track size. */
      top: 50%;
      margin-top: -12px;
    }
    .MuiSwitch-thumb {
      background-color: #fff;
      height: 20px;
      width: 20px;
      box-shadow: none;
    }
    .Mui-checked {
      .MuiSwitch-thumb {
        background-color: #fff;
        position: relative;

        &::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 16px;
          height: 16px;
          mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'%3E%3Cpath d='M12.2068 4.79279C12.3945 4.98031 12.5 5.23462 12.5 5.49979C12.5 5.76495 12.3945 6.01926 12.2068 6.20679L7.19985 11.2068C7.01206 11.3943 6.7574 11.4996 6.49187 11.4996C6.22634 11.4996 5.97167 11.3943 5.78388 11.2068L3.7811 9.20679C3.59869 9.01818 3.49776 8.76558 3.50004 8.50339C3.50232 8.24119 3.60763 7.99038 3.7933 7.80497C3.97897 7.61956 4.23013 7.51439 4.49269 7.51211C4.75525 7.50983 5.0082 7.61063 5.19707 7.79279L6.49187 9.08579L10.7908 4.79279C10.9786 4.60532 11.2333 4.5 11.4988 4.5C11.7644 4.5 12.019 4.60532 12.2068 4.79279Z' fill='white'/%3E%3C/svg%3E");
          mask-size: contain;
          mask-repeat: no-repeat;
          mask-position: center;
          background-color: var(--dct--customized-colors-buttonsLinks, ${({ theme }) =>
            theme.customized.colors.buttonsLinks});
        }
      }
    }
  }
`;
